import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import {
  Plus,
  FileText,
  Trash2,
  Copy,
  Loader2,
  Search,
  Layout,
  Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function OfferTemplates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/offers/templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Load templates error:", error);
      toast.error("Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to delete this template?")) return;
    
    try {
      await axios.delete(`${API}/offers/${templateId}`);
      toast.success("Template deleted");
      loadTemplates();
    } catch (error) {
      console.error("Delete template error:", error);
      toast.error("Failed to delete template");
    }
  };

  const createFromTemplate = async (templateId) => {
    try {
      const response = await axios.post(`${API}/offers/from-template/${templateId}`);
      
      if (response.data.offer_id) {
        toast.success("Offer created from template");
        navigate(`/offers/build/${response.data.offer_id}`);
      }
    } catch (error) {
      console.error("Create from template error:", error);
      toast.error("Failed to create from template");
    }
  };

  const filteredTemplates = templates.filter(template =>
    template.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.templateName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Offer Templates</h1>
          <p className="text-white/60 text-sm mt-1">
            {templates.length} template{templates.length !== 1 ? "s" : ""} available
          </p>
        </div>
        
        <Button
          onClick={() => navigate("/offers/build")}
          className="bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Offer
        </Button>
      </div>
      
      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search templates..."
          className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:border-[#00d4aa] focus:outline-none"
        />
      </div>
      
      {/* Info Card */}
      <Card className="card mb-6 bg-purple-500/10 border-purple-500/30">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-400 mt-0.5" />
            <div>
              <h3 className="text-white font-medium mb-1">What are templates?</h3>
              <p className="text-white/60 text-sm">
                Templates let you save successful offer structures and reuse them for similar clients. 
                Save any completed offer as a template from the Saved Offers page.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Card className="card">
          <CardContent className="py-12 text-center">
            <Layout className="w-12 h-12 text-white/20 mx-auto mb-4" />
            <h3 className="text-white font-medium mb-2">No templates yet</h3>
            <p className="text-white/60 text-sm mb-6">
              {searchQuery 
                ? "Try a different search term" 
                : "Create an offer and save it as a template to reuse later"
              }
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate("/offers/saved")}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Saved Offers
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card
              key={template.offer_id}
              className="card hover:border-purple-500/40 transition-all group"
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/20">
                      <Layout className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold line-clamp-1">
                        {template.templateName || template.clientName || "Untitled Template"}
                      </h3>
                      <p className="text-white/60 text-sm">{template.industry || "General"}</p>
                    </div>
                  </div>
                </div>
                
                {/* Template Info */}
                <div className="space-y-2 mb-4 p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/60">Original Client</span>
                    <span className="text-white">{template.clientName || "N/A"}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/60">Created</span>
                    <span className="text-white">
                      {new Date(template.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => createFromTemplate(template.offer_id)}
                    className="flex-1 bg-purple-500 hover:bg-purple-600 text-white font-semibold"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Use Template
                  </Button>
                  <Button
                    onClick={() => deleteTemplate(template.offer_id)}
                    variant="ghost"
                    size="icon"
                    className="text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default OfferTemplates;
